import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import { refreshUser } from 'src/api/auth';
import { FullScreenLoader } from 'src/components';
import { configuration, getLoginUrl, getLogoutUrl } from 'src/configuration';
import { useLocalCookies } from 'src/hooks/use-local-cookies';
import { signIn } from 'src/store/auth';
import { selectAccount } from 'src/store/selectors';
import { COOKIE_KEYS } from 'src/utils/cookies';
import { parseJwt } from 'src/utils/parse-jwt';
import { isIncompleteUser } from 'src/utils/user-handling';

export function AuthenticatorV2() {
  const dispatch = useDispatch();
  const location = useLocation();
  const account = useSelector(selectAccount);

  const [isAuthenticating, setIsAuthenticating] = useState(true);

  const { cookies, getSessionValues, removeAuthCookies } = useLocalCookies();
  const session = cookies[COOKIE_KEYS.AUTH.SESSION];

  const redirectToSignIn = () => {
    const { clientId } = getSessionValues();
    removeAuthCookies();
    window.location.replace(getLoginUrl(clientId, location.pathname));
  };

  const redirectToSignOut = () => {
    const { clientId } = getSessionValues();
    removeAuthCookies();
    window.location.replace(getLogoutUrl(clientId, location.pathname));
  };

  const redirectToCompleteProfile = () => {
    const { clientId } = getSessionValues();
    window.location.replace(
      `${configuration.appUrl}/complete-account-creation?client_id=${clientId}&redirect_url=${configuration.appUrl}${location.pathname}`,
    );
  };

  const refreshUserData = async () => {
    try {
      const { clientId, refreshToken } = getSessionValues();

      if (!refreshToken) {
        redirectToSignIn();
      }

      const refreshedUser = await refreshUser(refreshToken, clientId);

      const parsedUser = parseJwt(refreshedUser.id_token);

      if (!parsedUser['custom:researchers'] && !parsedUser['custom:participants']) {
        redirectToSignIn();
      }

      dispatch(
        signIn({
          email: parsedUser.email,
          givenName: parsedUser.given_name,
          familyName: parsedUser.family_name,
          phoneNumber: parsedUser.phone_number,
          groups: parsedUser['cognito:groups'],
          userId: parsedUser.sub,
          isSSO: !!parsedUser.identities,

          expires: Number(parsedUser.exp + '000'),
          accessToken: refreshedUser.access_token,
          idToken: refreshedUser.id_token,

          participantId: parsedUser['custom:participants'] || '',
          researcherId: parsedUser['custom:researchers'] || '',
          organizationIds: parsedUser['custom:organizations']?.split('|') || [],
          organizationAdminIds: parsedUser['custom:organizations-admin']?.split('|') || [],
        }),
      );

      if (isIncompleteUser(parsedUser)) {
        redirectToCompleteProfile();
      }

      setIsAuthenticating(false);
    } catch (error) {
      redirectToSignOut();
    }
  };

  // When going in for the first time, gets a new access token
  useEffect(() => {
    refreshUserData();
  }, []);

  // If session changes and there is no session, sign out
  useEffect(() => {
    if (!isAuthenticating) {
      if (session) {
        refreshUserData();
      } else {
        redirectToSignOut();
      }
    }
  }, [session]);

  // Periodically refreshes authentication token, checks every 10 seconds
  // Checks 90 seconds before expiring to ensure ample refresh time
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAuthenticating && Date.now() + 90000 > account.expires) {
        refreshUserData();
      }
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticating, account.expires]);

  const handleViteError = (event: Event) => {
    event.preventDefault();
    window.location.reload();
  };

  // TODO: Fix vite crash, temporal solution
  // https://vitejs.dev/guide/build.html#load-error-handling
  useEffect(() => {
    window.addEventListener('vite:preloadError', handleViteError);

    return () => {
      window.removeEventListener('vite:preloadError', handleViteError);
    };
  }, []);

  if (isAuthenticating) {
    return <FullScreenLoader />;
  }

  return <Outlet />;
}
